import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserClock,
    faTrash,
    faPencil
} from '@fortawesome/free-solid-svg-icons';
import { BaseFormViewModel } from '../common/ViewModel';
import SlideForm from '../common/forms/SlideForm';
import authService from '../api-authorization/AuthorizeService';
import { util } from '../Util';
import moment from 'moment';
import Select from 'react-select';
import {
    FormLabel,
    CollapseUnderlineHeader,
    FlexCenterRow,
    toasty
} from '../common/forms/FormElements';
import CommonContext, {
    ApiRoutes,
} from '../Common';
import {
    Button,
    FormGroup,
    Input,
    Badge
} from 'reactstrap';
import '../dailyDrivingLog/DailyDrivingLogForm.scss';
import TimesheetEdit from './TimesheetEdit';
import { TimesheetDetailsAdjustment, TimesheetChargeAdjustment } from '../timesheetManagement/TimesheetDetailsAdjustment';
import ValidatedSelect, {
    CompactSelectStyles,
} from '../common/forms/ValidatedSelect';
import NumericInput from '../common/forms/NumericInput';
import { HoursValues12Hour } from '../common/forms/TimeEntry';

// Convert a fractional-hour increment (e.g. 0.083 = 5 min, 0.25 = 15 min,
// 1.000 = 60 min) into the number of minutes per step. Charge types store the
// increment as a fraction of an hour, so we round to the nearest minute.
const incrementToMinutes = (increment) => {
    const inc = parseFloat(increment);
    if (!inc || inc <= 0) return null;
    const mins = Math.round(inc * 60);
    return mins >= 1 ? mins : null;
};

// Build the ordered list of 'h:mm A' option strings for a given increment.
// The minute slots are generated from the increment's minutes-per-step, so any
// charge-type interval works (5, 10, 15, 30, 60, ...), not just a fixed set.
const buildTimeOptions = (increment) => {
    const stepMinutes = incrementToMinutes(increment);
    if (!stepMinutes) return [];

    // Minute marks within an hour at this step. For 60+ min steps, only :00.
    const minuteMarks = [];
    for (let m = 0; m < 60; m += stepMinutes) {
        minuteMarks.push(String(m).padStart(2, '0'));
    }

    const options = [];
    HoursValues12Hour.forEach((h) => minuteMarks.forEach((m) => options.push(`${h}:${m} AM`)));
    HoursValues12Hour.forEach((h) => minuteMarks.forEach((m) => options.push(`${h}:${m} PM`)));
    options.sort((a, b) => moment(a, ['h:mm A']) - moment(b, ['h:mm A']));
    return options;
};

// A drop-in replacement for TimeEntry that additionally lets the user TYPE a
// time and filter the list (or enter one not in the list). It keeps the same
// contract as TimeEntry: `value` is stored 24-hour ('HH:mm') and displayed as
// 'h:mm A', and onChange is called with an event-shaped object
// { target: { name, value } } whose value is an 'h:mm A' string (or '' when
// cleared) — exactly what the page's existing change handlers parse.
//
// Scoped to TimesheetDetails only; the shared TimeEntry component is untouched.
const TimeEntryCombo = ({ name, value, increment, onChange, required, disabled, validationMessage }) => {
    const optionStrings = buildTimeOptions(increment);
    const options = optionStrings.map((o) => ({ value: o, label: o }));

    // Extend the shared compact styles locally so the selected time isn't
    // clipped. In a searchable select the singleValue shares the flex
    // value-container with the text input; without these the value gets
    // compressed and ellipsis-truncated to something like "7.". We let the
    // value show in full, keep it on one line, and stop the input from
    // stealing the row's width when a value is present.
    const timeSelectStyles = {
        ...CompactSelectStyles,
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflow: 'visible',
        }),
        singleValue: (base) => ({
            ...base,
            position: 'static',
            maxWidth: 'none',
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
            transform: 'none',
        }),
        input: (base) => ({
            ...base,
            minWidth: '2px',
        }),
    };

    // The stored `value` can be 24-hour ('HH:mm', after an edit) or 12-hour
    // ('h:mm a'/'h:mm A', as seeded on load). Normalize to canonical 'h:mm A'.
    const toCanonical = (v) => {
        if (v == null || v === '') return '';
        const m = moment(String(v).trim(), ['h:mm A', 'h:mm a', 'HH:mm', 'H:mm', 'h:mm'], false);
        return m.isValid() ? m.format('h:mm A') : '';
    };

    const canonical = toCanonical(value);
    // Prefer the matching option object from the list so react-select renders
    // the exact same label it shows in the menu; fall back to a standalone
    // option object if the time isn't one of the increments.
    const selected = canonical
        ? (options.find((o) => o.value === canonical) || { value: canonical, label: canonical })
        : null;

    const emit = (val) => {
        // Mirror the native-select event shape the handlers expect.
        onChange({ target: { name, value: val } });
    };

    // Normalize typed text to a canonical 'h:mm A' string if it parses.
    const normalizeTyped = (raw) => {
        if (raw == null) return '';
        const trimmed = String(raw).trim();
        if (trimmed === '') return '';
        const m = moment(trimmed, ['h:mm A', 'h:mm a', 'h:mmA', 'h:mma', 'H:mm', 'HH:mm', 'h:mm'], false);
        return m.isValid() ? m.format('h:mm A') : '';
    };

    // Track what the user is typing so we can commit it on blur. react-select
    // does NOT commit a highlighted/typed option unless the user presses Enter
    // or clicks it; if they type a valid time and then click Save (blurring the
    // control), the input would otherwise be discarded and the field revert to
    // its prior value. We commit an exact list match on blur to prevent that.
    const [typed, setTyped] = React.useState('');

    const commitTyped = () => {
        const canon = normalizeTyped(typed);
        // Only commit when the typed time is actually one of the listed options.
        if (canon && options.some((o) => o.value === canon)) {
            if (canon !== canonical) {
                emit(canon);
            }
        }
        setTyped('');
    };

    const handleChange = (option) => {
        // Only listed options are selectable, so option.value is always a valid
        // canonical 'h:mm A' time (or null when cleared).
        setTyped('');
        emit(option == null ? '' : option.value);
    };

    return (
        <Select
            styles={timeSelectStyles}
            className="time-input-entry"
            name={name}
            isDisabled={disabled}
            isClearable={!required}
            isSearchable
            placeholder="--"
            options={options}
            value={selected}
            onChange={handleChange}
            inputValue={typed}
            onInputChange={(input, meta) => {
                // Keep the user's typing; ignore programmatic resets (blur/close)
                // so we control clearing ourselves via commitTyped.
                if (meta.action === 'input-change') {
                    setTyped(input);
                }
            }}
            onBlur={commitTyped}
            getOptionLabel={(o) => String(o.label ?? o.value ?? '')}
            getOptionValue={(o) => String(o.value ?? '')}
            maxMenuHeight={200}
            menuPlacement="auto"
        />
    );
};

export default class TimesheetDetails extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();
        this.timesheetEditRef = React.createRef(); 

        const stateBase = {
            loading: false,
            saving: false,
            chargeTypes: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
    }

    // #region METHODS

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        //this.populateState();
    }

    componentWillUnmount() {
        return this.setState = (state, callback) => {

        };
    }

    open = async (date, employeeName, employeeId) => {

        const isAuthenticated = await authService.isAuthenticated();

        if (!isAuthenticated) {
            return;
        }

        this.props.toggleShow(true);
        this.context.setFormOpened(true);

        // Clear stale data and show loading overlay immediately so the
        // prior user's timesheet isn't visible while the new data loads.
        this.setState({
            loading: true,
            saving: false,
            dailyEntries: [],
            weeklyTotals: null,
            employee: null,
            employeeName: employeeName,
            employeeId: employeeId,
            date: date,
            chargesOpen: true,
            isAdjustingCharge: false,
        });

        const jobDate = moment(date).format('YYYYMMDD');

        let [dailyEntries, weeklyTotals, nonFlaggingContract, employee] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byEmployee(jobDate, employeeId)),
            util.fetch.js(ApiRoutes.dailyDrivingLog.weeklyTotals(employeeId, jobDate)),
            util.fetch.js(ApiRoutes.contract.getNonFlagging()),
            util.fetch.js(ApiRoutes.employee.byId(employeeId)),
        ]);

        for (var d = 0; d < dailyEntries.length; d++) {
            dailyEntries[d].adjusting = false;
            dailyEntries[d].adjustingUnitsType = 0;
            dailyEntries[d].adjustment = new TimesheetDetailsAdjustment();
        }

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState((state) => ({
            date: date,
            loading: false,
            saving: false,
            chargesOpen: true,
            dailyEntries: dailyEntries,
            weeklyTotals: weeklyTotals,
            jobDate: jobDate,
            employeeId: employeeId,
            employeeName: employeeName,
            employee,
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            nonFlaggingContract: nonFlaggingContract,
            isAdjustingCharge: false
        }));

    };

    onClose = () => {
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        this.props.onClose();
    };

    hourlyChargesTotal = (hourlyJob) => {

        var totalHours = 0.0;

        const hourlyTickets = (hourlyJob.scaleTickets ?? []).filter((x) => x.isTonnage == false);

        for (var t = 0; t < hourlyTickets.length; t++) {
            var hourlyCharges = hourlyTickets[t].charges;

            for (var hc = 0; hc < hourlyCharges.length; hc++) {
                if (hourlyCharges[hc].resourceTypeId == 1 && hourlyCharges[hc].removed != true)
                    totalHours += hourlyCharges[hc].value;
            }
        }
        
        return totalHours;
    }

    tonnageChargesTotal = (job) => {

        var totalHours = 0.0;

        const tonnageTickets = (job.scaleTickets ?? []).filter((x) => x.isTonnage == true);

        var start = null;
        for (var s = 0; s < tonnageTickets.length; s++) {
            var scaleTicket = tonnageTickets[s];

            for (var c = 0; c < scaleTicket.charges.length; c++) {
                var charge = scaleTicket.charges[c];

                if (charge.chargeTypeUnitsId == 3 || charge.linkedChargeTypeId > 0 || charge.removed == true)
                    continue;

                if (start == null && charge.start != null) {
                    start = charge.start;
                }

                if (charge.end != null) {

                    // Use the full datetimes (not stripped time-of-day) so that
                    // entries crossing midnight compute their real duration.
                    var startDt = moment(start);
                    var endDt = moment(charge.end);

                    // Defensive: if legacy data has End anchored to the wrong
                    // day (End < Start), roll forward day-by-day until it's
                    // after Start. New saves go through the backend midnight
                    // check, so this only matters for old records.
                    while (endDt.isBefore(startDt)) {
                        endDt.add(1, 'day');
                    }

                    var duration = moment.duration(endDt.diff(startDt));
                    if (duration.asHours() > 0 && duration.asHours() < 24)
                        totalHours += duration.asHours();

                    start = null;
                }
            }
        }

        return totalHours;
    }

    getTotalHours = () => {
        const { dailyEntries } = { ...this.state };
        var totalHours = 0;

        // Internal
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);
        for (var ch = 0; ch < charges.length; ch++) {

            if (charges[ch].removed == true)
                continue;

            totalHours += charges[ch].value;
        }

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        // Hourly
        for (var hj = 0; hj < jobs.length; hj++) {
            var hourlyJob = jobs[hj];
            const hourlyTickets = (hourlyJob.scaleTickets ?? []).filter((x) => x.isTonnage == false);

            for (var t = 0; t < hourlyTickets.length; t++) {
                var hourlyCharges = hourlyTickets[t].charges;

                for (var hc = 0; hc < hourlyCharges.length; hc++) {
                    if (hourlyCharges[hc].resourceTypeId == 1 && hourlyCharges[hc].removed != true)
                        totalHours += hourlyCharges[hc].value;
                }
            }
        }

        // Tonnage
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            const scaleTickets = (job.scaleTickets ?? []).filter((x) => x.isTonnage == true);

            var start = null;
            for (var s = 0; s < scaleTickets.length; s++) {
                var scaleTicket = scaleTickets[s];

                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];

                    if (charge.chargeTypeUnitsId == 3 || charge.linkedChargeTypeId > 0 || charge.removed == true)
                        continue;

                    if (start == null && charge.start != null) {
                        start = charge.start;
                    }

                    if (charge.end != null) {

                        // Use the full datetimes (not stripped time-of-day) so
                        // that entries crossing midnight compute their real
                        // duration.
                        var startDt = moment(start);
                        var endDt = moment(charge.end);

                        // Defensive: if legacy data has End anchored to the
                        // wrong day (End < Start), roll forward day-by-day
                        // until it's after Start. New saves go through the
                        // backend midnight check, so this only matters for
                        // old records.
                        while (endDt.isBefore(startDt)) {
                            endDt.add(1, 'day');
                        }

                        var duration = moment.duration(endDt.diff(startDt));
                        if (duration.asHours() > 0 && duration.asHours() < 24)
                            totalHours += duration.asHours();

                        start = null;
                    }
                }
            }
        }

        return totalHours.toFixed(2);
    }

    
    getTotalTonnage = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalTonnage = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                for (var c = 0; c < scaleTicket.charges.length; c++) {
                    var charge = scaleTicket.charges[c];
                    if (charge.chargeTypeUnitsId == 3 && charge.removed != true) {
                        totalTonnage += charge.tonnage;
                    }
                }
            }
        }

        return totalTonnage.toLocaleString('en-US');
    }

    getTotalLoads = () => {
        const { dailyEntries } = { ...this.state };

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);

        var totalLoads = 0;
        for (var j = 0; j < jobs.length; j++) {
            var job = jobs[j];
            for (var s = 0; s < job.scaleTickets.length; s++) {
                var scaleTicket = job.scaleTickets[s];
                if (scaleTicket.isDeleted)
                    continue;

                if (scaleTicket.isTonnage) {
                    for (var c = 0; c < scaleTicket.charges.length; c++) {
                        var charge = scaleTicket.charges[c];

                        if (charge.chargeTypeUnitsId == 1 && charge.removed != true) {
                            totalLoads++;
                        }
                    }
                }

                else {
                    for (var c2 = 0; c2 < scaleTicket.charges.length; c2++) {
                        var charge2 = scaleTicket.charges[c2];

                        if (charge2.chargeTypeUnitsId == 1 && charge2.removed != true) {
                            totalLoads += charge2.loads;
                        }
                    }
                }
            }
        }

        return totalLoads;
    }

    async approveInternalCharges() {
        const { jobDate, employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.approveCharges(jobDate, employeeId),
            null,
            util.fetch.format.none,
        );
        
        toasty.success('Charges Approved');
        

        this.open(date, employeeName, employeeId);
    }

    async approveHourlyTimesheets(timesheetId) {
        const { employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        await util.fetch.post(
            ApiRoutes.dailyDrivingLog.approveHourlyTimesheets(timesheetId, employeeId),
            null,
            util.fetch.format.none,
        );

        toasty.success('Hourly Charges Approved');

        this.open(date, employeeName, employeeId);
    }

    async approveTonnageTimesheets(timesheetId) {
        const { employeeId, date, employeeName } = { ...this.state };

        this.setState({
            saving: true
        });

        try {
            var ret = await util.fetch.post(
                ApiRoutes.dailyDrivingLog.approveTonnageTimesheets(timesheetId, employeeId),
                null,
                util.fetch.format.none,
            );

            if (!ret.ok)
                toasty.error('Unable to approve. End time range not provided.');
            else
                toasty.success('Tonnage Charges Approved');
        } catch (err) {
            toasty.error('Unable to approve. End time range not provided.');
        }

        this.open(date, employeeName, employeeId);
    }

    async populateState() {
        
    }

    onTimesheetEdit = async (job, timesheet) => {

        job.signedOn = timesheet.signedOn;
        job.signatureNotes = timesheet.signatureNotes;
        job.signatureData = timesheet.signatureData;

        this.timesheetEditRef.current.open(job);
    };

    editInternalCharge = async (charge) => {
        const { date, dailyEntries, timesheetChargeAdjustment, employee } = { ...this.state };

        timesheetChargeAdjustment.removeCharge = charge;
        timesheetChargeAdjustment.timesheetId = charge.timesheetId;
        timesheetChargeAdjustment.jobDate = date;
        timesheetChargeAdjustment.chargeTypeId = charge.chargeTypeId;
        timesheetChargeAdjustment.notes = 'Adjustment to charge';
        timesheetChargeAdjustment.chargeTypeName = charge.chargeTypeName;
        timesheetChargeAdjustment.resourceId = charge.chargeTypeId;
        timesheetChargeAdjustment.resourceName = charge.chargeTypeName;
        timesheetChargeAdjustment.equipmentId = charge.equipmentId;
        timesheetChargeAdjustment.equipmentName = charge.equipmentName;
        timesheetChargeAdjustment.startDate = moment.utc(charge.startDate);
        timesheetChargeAdjustment.endDate = moment.utc(charge.endDate);
        timesheetChargeAdjustment.quantity = charge.value;
        timesheetChargeAdjustment.start = moment(charge.startDate).format('h:mm a');
        timesheetChargeAdjustment.end = moment(charge.endDate).format('h:mm a');
        timesheetChargeAdjustment.quantity = charge.value;
        timesheetChargeAdjustment.chargeTypeObj = {
            resourceTypeId: charge.resourceTypeId,
            description: charge.chargeName,
            unitsId: charge.chargeTypeUnitsId,
            // Carry the charge type's time increment through so the time picker
            // uses the correct interval. Falls back to 5-min in the combo if
            // the source charge doesn't provide it.
            chargeTypeIncrement: charge.chargeTypeIncrement,
            units: {
                description: charge.chargeTypeUnitsName,
            }
        };

        let [equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipmentByEmployee(employee.dispatchCompanyId, employee.id)),
        ]);

        this.setState({
            dailyEntries,
            equipment,
            timesheetChargeAdjustment,
            isAdjustingCharge: true
        });
    };

    editCharge = async (job, scaleTicket, charge, unitsType) => {
        const { dailyEntries } = { ...this.state };

        job.adjustment.removeCharge = charge;
        job.adjustment.timesheetId = scaleTicket.timesheetId;
        job.adjustment.jobDate = scaleTicket.jobDate;
        job.adjustment.jobAssignmentId = charge.assignmentId;
        job.adjustment.chargeTypeId = charge.chargeId;
        job.adjustment.notes = 'Adjustment to Job: ' + scaleTicket.timesheetNumber + ', Ticket: ' + scaleTicket.workOrderNumber;
        job.adjustment.chargeTypeName = charge.chargeName;
        job.adjustment.resourceId = charge.resourceId;
        job.adjustment.resourceName = charge.resourceName;
        job.adjustment.materialId = charge.materialId;
        job.adjustment.materialName = charge.materialName;
        job.adjustment.loads = charge.loads;
        job.adjustment.startDate = moment.utc(charge.start);
        job.adjustment.endDate = charge.end == null ? null : moment.utc(charge.end);
        job.adjustment.quantity = charge.value;
        job.adjustment.start = charge.chargeTypeUnitsId == 1 ? moment(charge.start).format('h:mm a') : '';
        job.adjustment.end = charge.end == null ? null : charge.chargeTypeUnitsId == 1 ? moment(charge.end).format('h:mm a') : '';
        job.adjustment.quantity = charge.value;
        job.adjustment.chargeTypeObj = {
            resourceTypeId: charge.resourceTypeId,
            description: charge.chargeName,
            unitsId: charge.chargeTypeUnitsId,
            // Carry the charge type's time increment through so the time picker
            // uses the correct interval (falls back to 5-min if absent).
            chargeTypeIncrement: charge.chargeTypeIncrement,
            units: {
                description: charge.chargeTypeUnitsName,
            }
        };

        let [chargeTypes, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.chargeType.byContract(job.contractId)),
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipment(job.dispatchCompanyId)),
        ]);

        const haulNumbers = [];
        for (var i = 0; i < job.scaleTickets.length; i++) {
            haulNumbers.push({
                id: job.scaleTickets[i].timesheetId,
                description: job.scaleTickets[i].timesheetNumber,
            });
        }

        job.adjustingUnitsType = unitsType;
        job.adjusting = true;

        this.setState({
            dailyEntries,
            chargeTypes,
            equipment,
            haulNumbers
        });
    };

    onAdjustInternalCharge = async () => {

        const { employee } = { ...this.state };

        let [equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipmentByEmployee(employee.dispatchCompanyId, employee.id)),
        ]);

        this.setState({
            equipment,
            isAdjustingCharge: true,
        });
    }

    cancelInternalChargeAdjustment = async () => {

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState(() => ({
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            isAdjustingCharge: false
        }));
    };

    onAdjustClick = async (job, unitsType) => {
        const { dailyEntries } = { ...this.state };

        let [chargeTypes, equipment] = await Promise.all([
            util.fetch.js(ApiRoutes.chargeType.byContract(job.contractId)),
            util.fetch.js(ApiRoutes.timesheet.slideoutEquipment(job.dispatchCompanyId)),
        ]);

        const haulNumbers = [];
        for (var i = 0; i < job.scaleTickets.length; i++) {
            haulNumbers.push({
                id: job.scaleTickets[i].timesheetId,
                description: job.scaleTickets[i].timesheetNumber,
            });
        }

        job.adjustingUnitsType = unitsType;
        job.adjusting = true;

        this.setState({
            dailyEntries,
            chargeTypes,
            equipment,
            haulNumbers
        });
    }

    saveInternalChargeAdjustment = async () => {

        const { date, nonFlaggingContract, timesheetChargeAdjustment } = { ...this.state };

        if (parseFloat(timesheetChargeAdjustment.quantity) == 0) {
            toasty.error('Start time cannot be greater than end time');
            return;
        }

        if (timesheetChargeAdjustment.chargeTypeObj.resourceId == 0
            || timesheetChargeAdjustment.notes == null || timesheetChargeAdjustment.notes.length == 0)
            return;

        // Start and End are required. An empty box (e.g. after the user typed a
        // time that didn't match the allowed intervals and it was rejected)
        // leaves these blank while the previously stored startDate/endDate
        // moments still linger — saving anyway would silently keep the old time.
        // Block the save and tell the user so they don't think a bad value saved.
        if (timesheetChargeAdjustment.start == null || timesheetChargeAdjustment.start === ''
            || timesheetChargeAdjustment.end == null || timesheetChargeAdjustment.end === '') {
            toasty.error('Start and End times are required.');
            return;
        }

        // Validate that stop time is greater than start time.
        // start/end can be in 'HH:mm' (after edit) or 'h:mm a' (initial load) — try both.
        //const mStart = moment(timesheetChargeAdjustment.start, ['HH:mm', 'h:mm a']);
        //const mEnd = moment(timesheetChargeAdjustment.end, ['HH:mm', 'h:mm a']);
        //if (mEnd.isSameOrBefore(mStart)) {
        //    toasty.error('Stop time must be greater than start time');
        //    return;
        //}

        if (timesheetChargeAdjustment.equipmentId == 0) {
            return;
        }

        const { employeeId } = { ...this.state };

        var start = moment(timesheetChargeAdjustment.startDate).utc().format('HH:mm');
        var end = moment(timesheetChargeAdjustment.endDate).utc().format('HH:mm');
        // Send the selected calendar dates (job date or the day after) so the
        // server can place start/end on the correct day instead of stamping the
        // timesheet date onto both. Date-only, matching the time format above.
        var startDate = moment(timesheetChargeAdjustment.startDate).utc().format('YYYY-MM-DD');
        var endDate = moment(timesheetChargeAdjustment.endDate).utc().format('YYYY-MM-DD');

        this.setState({
            saving: true
        });

        await util.fetch.andGetResponse(
            util.fetch.types.post,
            ApiRoutes.selfDispatchTimesheet.create(),
            {
                isAdjustment: true,
                include: true,
                contractId: nonFlaggingContract.id,
                contractDescription: nonFlaggingContract.description,
                date: date,
                isComplete: false,
                isDeleted: false,
                nonFlagging: true,
                employeeId: employeeId,
                timeEntries: [{
                    chargeTypeId: timesheetChargeAdjustment.resourceId,
                    chargeTypeName: timesheetChargeAdjustment.resourceName,
                    equipmentId: timesheetChargeAdjustment.equipmentId,
                    equipmentName: timesheetChargeAdjustment.equipmentName,
                    employeeId: employeeId,
                    chargeTypeUnitsId: 1,
                    chargeTypeUnitsName: 'Hours',
                    notes: timesheetChargeAdjustment.notes,
                    start: start,
                    end: end,
                    startDate: startDate,
                    endDate: endDate
                }]
            },
            'Error Creating Timesheet',
        );

        
        this.refresh();
    };

    saveAdjustment = async (job, isTonnage) => {

        if (!isTonnage && parseFloat(job.adjustment.quantity) == 0) {
            toasty.error('Start time cannot be greater than end time');
            return;
        }

        if (job.adjustment.timesheetId == 0
            || job.adjustment.chargeTypeObj == null
            || job.adjustment.notes == null || job.adjustment.notes.length == 0)
            return;

        // For hourly charges (unitsId == 1) Start and End are required. An empty
        // box — e.g. after the user typed a time that didn't match the allowed
        // intervals and it was rejected — leaves these blank while the previously
        // stored startDate/endDate still linger, so saving would silently keep
        // the old time. Block the save and tell the user. Tonnage entries don't
        // use start/end times, so this check is hourly-only.
        const isHourlyAdjustment = job.adjustment.chargeTypeObj?.unitsId == 1;
        if (isHourlyAdjustment) {
            const startEmpty = job.adjustment.start == null || job.adjustment.start === '';
            const endEmpty = job.adjustment.end == null || job.adjustment.end === '';
            if (startEmpty || endEmpty) {
                toasty.error('Start and End times are required.');
                return;
            }
        }

        // Validate that stop time is greater than start time.
        // For tonnage, end is optional — skip the check when end is omitted.
        // start/end can be in 'HH:mm' (after edit) or 'h:mm a' (initial load) — try both.
        //if (job.adjustment.start && job.adjustment.end) {
        //    const mStart = moment(job.adjustment.start, ['HH:mm', 'h:mm a']);
        //    const mEnd = moment(job.adjustment.end, ['HH:mm', 'h:mm a']);
        //    if (mEnd.isSameOrBefore(mStart)) {
        //        toasty.error('Stop time must be greater than start time');
        //        return;
        //    }
        //}

        const { employeeId, date } = { ...this.state };

        let tsJobDate = null;
        if (job.scaleTickets.length > 0) {
            tsJobDate = job.scaleTickets[0].jobDate;
        }

        // If the job has no scale tickets yet (e.g. brand-new tonnage job
        // where the user is adding the first punch), fall back to the page's
        // viewed date. Without this fallback, moment(null) gives today's
        // date and the Start would be stamped with today instead of the
        // actual job date.
        if (tsJobDate == null) {
            tsJobDate = date;
        }

        // Use the start datetime as built from the time entry and the date
        // dropdown. Do NOT re-stamp the job date here — doing so would discard
        // a next-day selection the user made in the start date dropdown.
        var startDate = moment(job.adjustment.startDate);

        this.setState({
            saving: true
        });

        if (isTonnage && job.adjustment.endDate == null) {

            //Save
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.adjustment(),
                {
                    timesheetId: job.adjustment.timesheetId,
                    jobDate: tsJobDate,
                    start: job.adjustment.startDate ? moment(startDate).toDate().toISOString() : null,
                    end: null,
                    resourceName: job.adjustment.resourceName,
                    resourceId: job.adjustment.resourceId ? job.adjustment.resourceId : 0,
                    resourceTypeId: job.adjustment.chargeTypeObj.resourceTypeId,
                    chargeTypeName: job.adjustment.chargeTypeName,
                    chargeTypeId: job.adjustment.chargeTypeId,
                    quantity: parseFloat(job.adjustment.quantity),
                    notes: job.adjustment.notes,
                    unitsId: job.adjustment.chargeTypeObj.unitsId,
                    unitsName: job.adjustment.chargeTypeObj.units.description,
                    employeeId: employeeId,
                    loads: 1,
                    materialId: job.adjustment.materialId,
                    jobAssignmentId: job.adjustment.jobAssignmentId
                },
            );

        }

        else {
            // Use the end datetime as built from the time entry and the date
            // dropdown. Do NOT re-stamp the job date here — that would discard
            // a next-day selection the user made in the end date dropdown.
            var endDate = moment(job.adjustment.endDate);

            //Save
            await util.fetch.post(
                ApiRoutes.dailyDrivingLog.adjustment(),
                {
                    timesheetId: job.adjustment.timesheetId,
                    jobDate: tsJobDate,
                    start: job.adjustment.startDate ? moment(startDate).toDate().toISOString() : null,
                    end: job.adjustment.endDate ? moment(endDate).toDate().toISOString() : null,
                    resourceName: job.adjustment.resourceName,
                    resourceId: job.adjustment.resourceId ? job.adjustment.resourceId : 0,
                    resourceTypeId: job.adjustment.chargeTypeObj.resourceTypeId,
                    chargeTypeName: job.adjustment.chargeTypeName,
                    chargeTypeId: job.adjustment.chargeTypeId,
                    quantity: parseFloat(job.adjustment.quantity),
                    notes: job.adjustment.notes,
                    unitsId: job.adjustment.chargeTypeObj.unitsId,
                    unitsName: job.adjustment.chargeTypeObj.units.description,
                    employeeId: employeeId,
                    loads: parseInt(job.adjustment.loads),
                    materialId: job.adjustment.materialId,
                    jobAssignmentId: job.adjustment.jobAssignmentId
                },
            );
        }

        if (job.adjustment?.removeCharge)
            await this.removeCharge(job.adjustment.removeCharge);

        this.refresh();
    };

    refresh = async () => {
        const { employeeId, jobDate } = { ...this.state };

        let [dailyEntries, weeklyTotals] = await Promise.all([
            util.fetch.js(ApiRoutes.dailyDrivingLog.byEmployee(jobDate, employeeId)),
            util.fetch.js(ApiRoutes.dailyDrivingLog.weeklyTotals(employeeId, jobDate))
        ]);

        for (var d = 0; d < dailyEntries.length; d++) {
            dailyEntries[d].adjustingUnitsType = 0;
            dailyEntries[d].adjusting = false;
            dailyEntries[d].adjustment = new TimesheetDetailsAdjustment();
        }

        var timesheetChargeAdjustment = new TimesheetChargeAdjustment();

        this.setState(() => ({
            dailyEntries: dailyEntries,
            weeklyTotals: weeklyTotals,
            timesheetChargeAdjustment: timesheetChargeAdjustment,
            isAdjustingCharge: false,
            saving: false
        }));
    }

    cancelAdjustment = async (dailyEntries, job) => {
        job.adjusting = false;
        job.adjustingUnitsType = 0;
        job.adjustment = new TimesheetDetailsAdjustment();

        this.setState(() => ({
            dailyEntries
        }));
    };

    onInternalChargeTimeEntryChanged = (ev) => {
        const { date, timesheetChargeAdjustment } = { ...this.state };

        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        timesheetChargeAdjustment[name] = timeValue;
        this.setAdjustmentTimeQuantity(date, timesheetChargeAdjustment);

        this.setState(() => ({
            timesheetChargeAdjustment
        }));
    };

    onAdjustmentTimeEntryChanged = (dailyEntries, j, ev) => {
        const val = ev.target.value;
        const { name } = ev.target;

        // We display the options in 12-hour format, but the db has 24.
        const timeValue = ev.target.value
            ? moment(val, ['h:mm A']).format('HH:mm')
            : '';

        if (name == 'end' && val == '') {
            j.adjustment.end = null;
            j.adjustment.endDate = null;
            j.adjustment.quantity = 0;

            this.setAdjustmentTimeQuantity(j.startDate, j.adjustment);
        }

        else {
            j.adjustment[name] = timeValue;
            this.setAdjustmentTimeQuantity(j.startDate, j.adjustment);
        }

        this.setState(() => ({
            dailyEntries
        }));
    };

    setAdjustmentTimeQuantity = (jobStart, adj) => {

        // Calculate the duration between Start and End.  Factor in if this enters into the next day.
        // Use the job start date/time as opposed to moment defaulting to the current day when getting duration.
        if (adj.start) {
            const time_format1 = 'HH:mm';
            const time_format2 = 'h:mm a';
            const date_format = 'YYYY-MM-DD';

            // Preserve the date portion the user already chose via the date
            // dropdown (held on adj.startDate / adj.endDate). Only fall back to
            // the job date when no date has been selected yet. Without this,
            // every time-field edit would re-stamp the job date and silently
            // discard a next-day selection.
            const mStartDate = adj.startDate != null
                ? moment.utc(adj.startDate)
                : moment.utc(jobStart);
            const mStartTime = adj.start.indexOf('m') == 0 ? moment.utc(adj.start, time_format1) : moment.utc(adj.start, time_format2);

            adj.startDate = moment.utc(
                `${mStartDate.format(date_format)} ${mStartTime.format(
                    time_format1,
                )}`,
            ).valueOf();

            if (adj.end == null || adj.end === '') {
                adj.endDate = null;
                adj.quantity = 0;
            }
            else {
                const mEndDate = adj.endDate != null
                    ? moment.utc(adj.endDate)
                    : moment.utc(mStartDate);
                const mEndTime = adj.end.indexOf('m') == 0 ? moment.utc(adj.end, time_format1) : moment.utc(adj.end, time_format2);

                // Only auto-roll to the next day when the user hasn't already
                // picked an explicit end date. If an end date is set, respect it.
                if (adj.endDate == null && mEndTime.isBefore(mStartTime)) {
                    mEndTime.add(1, 'day');
                    mEndDate.add(1, 'day');
                }

                adj.endDate = moment.utc(
                    `${mEndDate.format(date_format)} ${mEndTime.format(
                        time_format1,
                    )}`,
                ).valueOf();

                // Duration spans the full chosen start/end datetimes so a
                // next-day end is counted correctly.
                const dur = moment.duration(
                    moment.utc(adj.endDate).diff(moment.utc(adj.startDate)),
                );
                adj.quantity = dur.asHours().toFixed(2);
                
            }
        }
        else {
            // Start was cleared (e.g. an off-interval typed time was rejected,
            // leaving the box empty). Clear the derived start datetime and
            // quantity so a stale value can't be posted on save.
            adj.startDate = null;
            adj.quantity = 0;
        }
    };

    removeCharge = async (id) => {
        await util.fetch.post(ApiRoutes.dailyDrivingLog.removeCharge(id));
        this.refresh();
    };

    // Renders a dropdown of two dates (the adjustment's job date, and the day
    // after) bound to the date portion of adj.endDate. When the user picks a
    // different date, adj.endDate's date is updated to match (time portion
    // preserved) and quantity is recomputed. The dropdown reflects whatever
    // date is currently on adj.endDate, including auto-roll-to-next-day
    // results from setAdjustmentTimeQuantity.
    renderEndDateDropdown = (adj, applyStateChange) => {
        if (adj == null) return null;

        const jobDateRaw = adj.jobDate;
        if (jobDateRaw == null) return null;

        const date_format = 'YYYY-MM-DD';
        const display_format = 'M/DD/YYYY';
        const jobDate = moment.utc(jobDateRaw).format(date_format);
        const nextDate = moment.utc(jobDate).add(1, 'day').format(date_format);
        const jobDateLabel = moment.utc(jobDate).format(display_format);
        const nextDateLabel = moment.utc(nextDate).format(display_format);

        // What date is currently on adj.endDate? Snap to one of the two
        // options; if endDate isn't set, default to the job date.
        let currentDate = jobDate;
        if (adj.endDate != null) {
            const endDateStr = moment.utc(adj.endDate).format(date_format);
            currentDate = endDateStr === nextDate ? nextDate : jobDate;
        }

        const handleChange = (ev) => {
            const newDateStr = ev.target.value;
            // Preserve the existing end time-of-day on adj.endDate; only the
            // date portion changes. If endDate isn't set, there's nothing to
            // anchor to, so do nothing.
            if (adj.endDate == null) {
                return;
            }
            const currentEnd = moment.utc(adj.endDate);
            const timeOfDay = currentEnd.format('HH:mm');
            adj.endDate = moment.utc(`${newDateStr} ${timeOfDay}`).valueOf();

            // Recompute quantity from the new startDate/endDate. If both are
            // present, this is end - start in hours.
            if (adj.startDate != null && adj.endDate != null) {
                const mStart = moment.utc(adj.startDate);
                const mEnd = moment.utc(adj.endDate);
                const dur = moment.duration(mEnd.diff(mStart));
                adj.quantity = dur.asHours().toFixed(2);
            }

            applyStateChange();
        };

        return (
            <select
                className="form-control form-control-sm"
                value={currentDate}
                onChange={handleChange}
                style={{ marginTop: '4px', textAlign: 'right' }}
            >
                <option value={jobDate}>{jobDateLabel}</option>
                <option value={nextDate}>{nextDateLabel}</option>
            </select>
        );
    };

    // Renders a dropdown of two dates (the adjustment's job date, and the day
    // after) bound to the date portion of adj.startDate. When the user picks a
    // different date, adj.startDate's date is updated to match (time portion
    // preserved) and quantity is recomputed. Mirrors renderEndDateDropdown.
    renderStartDateDropdown = (adj, applyStateChange) => {
        if (adj == null) return null;

        const jobDateRaw = adj.jobDate;
        if (jobDateRaw == null) return null;

        const date_format = 'YYYY-MM-DD';
        const display_format = 'M/DD/YYYY';
        const jobDate = moment.utc(jobDateRaw).format(date_format);
        const nextDate = moment.utc(jobDate).add(1, 'day').format(date_format);
        const jobDateLabel = moment.utc(jobDate).format(display_format);
        const nextDateLabel = moment.utc(nextDate).format(display_format);

        // What date is currently on adj.startDate? Snap to one of the two
        // options; if startDate isn't set, default to the job date.
        let currentDate = jobDate;
        if (adj.startDate != null) {
            const startDateStr = moment.utc(adj.startDate).format(date_format);
            currentDate = startDateStr === nextDate ? nextDate : jobDate;
        }

        const handleChange = (ev) => {
            const newDateStr = ev.target.value;
            // Preserve the existing start time-of-day on adj.startDate; only the
            // date portion changes. If startDate isn't set, there's nothing to
            // anchor to, so do nothing.
            if (adj.startDate == null) {
                return;
            }
            const currentStart = moment.utc(adj.startDate);
            const timeOfDay = currentStart.format('HH:mm');
            adj.startDate = moment.utc(`${newDateStr} ${timeOfDay}`).valueOf();

            // Recompute quantity from the new startDate/endDate. If both are
            // present, this is end - start in hours.
            if (adj.startDate != null && adj.endDate != null) {
                const mStart = moment.utc(adj.startDate);
                const mEnd = moment.utc(adj.endDate);
                const dur = moment.duration(mEnd.diff(mStart));
                adj.quantity = dur.asHours().toFixed(2);
            }

            applyStateChange();
        };

        return (
            <select
                className="form-control form-control-sm"
                value={currentDate}
                onChange={handleChange}
                style={{ marginTop: '4px', textAlign: 'right' }}
            >
                <option value={jobDate}>{jobDateLabel}</option>
                <option value={nextDate}>{nextDateLabel}</option>
            </select>
        );
    };

    // Renders the adjustment's job date as static text styled to vertically
    // align with the end-date dropdown rendered in the end column. Start is
    // always anchored to the job date (no midnight crossing on the start
    // side), so this is informational only.
    renderStartDateLabel = (adj) => {
        if (adj == null) return null;

        const jobDateRaw = adj.jobDate;
        if (jobDateRaw == null) return null;

        const display_format = 'M/DD/YYYY';
        const jobDateLabel = moment.utc(jobDateRaw).format(display_format);

        return (
            <div
                className="form-control form-control-sm"
                style={{
                    marginTop: '4px',
                    border: '0',
                    backgroundColor: 'transparent',
                    padding: '0.25rem 0.5rem',
                    textAlign: 'right',
                }}
            >
                {jobDateLabel}
            </div>
        );
    };

    render() {
        const {
            loading,
            saving,
            chargesOpen,
            dailyEntries,
            weeklyTotals,
            jobDate,
            employeeId,
            employeeName,
            chargeTypes,
            equipment,
            haulNumbers, 
            isAdjustingCharge,
            timesheetChargeAdjustment,
            nonFlaggingContract
        } = this.state;

        const {
            showNav,
        } = { ...this.props };

        const tenantSettings = ((this.context ?? {}).tenant ?? {}).tenantSettings ?? {};

        var formattedDate = moment(jobDate, 'YYYYMMDD').format('dddd, MMMM Do YYYY');

        const jobs = (dailyEntries ?? []).filter((x) => x.logEntryType == 1);
        const charges = (dailyEntries ?? []).filter((x) => x.logEntryType == 2);

        const allChargesApproved = (charges ?? []).filter((x) => x.status == 'Approved').length == (charges ?? []).length;
        const allChargesSubmitted = charges.length > 0 && charges.filter((x) => x.status == 'Unsubmitted').length == 0;

        const totalTonnage = this.getTotalTonnage();

        var totalCharges = 0.00;
        for (var i = 0; i < charges.length; i++) {
            if (charges[i].removed == true)
                continue;
            totalCharges += charges[i].value;
        }

        return (
            <>
                <SlideForm
                    size="col-xl-12 col-md-12 col-xs-12"
                    loading={loading || !tenantSettings}
                    show={this.props.show}
                    id="timesheetForm"
                    formIcon={faUserClock}
                    formTitle="Daily Driving Log"
                    ref={this.formRef}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={true}
                    isValid={(value) => {
                        if (!value) {
                            toasty.error(
                                'Validation Failed. Please review and complete all required data',
                            );
                        }
                    }}
                    className="pb-2 w-100"
                    hideToolbar
                    onSubmit={this.onSubmit}
                    onClose={this.onClose}
                    errors={this.state.errors}
                    onClearErrors={this.onClearErrors}
                    readOnly={false}
                    showNav={showNav}
                    onNavBack={this.props.onNavBack}
                    onNavForward={this.props.onNavForward}
                    saveButtonText="Save Ticket"
                >
                    {loading && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 9999,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: 'all',
                            }}
                        >
                            <div style={{ textAlign: 'center', color: '#fff' }}>
                                <i className="fa fa-spinner fa-spin fa-3x" />
                                <div style={{ marginTop: '10px', fontSize: '16px' }}>Loading...</div>
                            </div>
                        </div>
                    )}

                    <div className="job-confirm-details" style={{ marginLeft: '15px', marginRight: '15px', marginTop: '5px' }}>

                        <table style={{ width: '450px', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                            <tbody style={{ verticalAlign: 'middle' }}>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Date" />
                                    </td>
                                    <td colSpan={3} style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {formattedDate ?? ''}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Driver" />
                                    </td>
                                    <td colSpan={3} style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {employeeName}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ width: '60px', padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Hours" />
                                    </td>
                                    <td style={{ width: '120px', padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {this.getTotalHours(dailyEntries)}
                                        </span>
                                    </td>
                                    <td style={{ width: '120px', padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Hours" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.hours?.toFixed(2)}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Tonnage" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {totalTonnage}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Tonnage" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.tonnage}
                                        </span>
                                    </td>
                                </tr>
                                <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Loads" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {this.getTotalLoads()}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 4px 4px', paddingLeft: '30px' }}>
                                        <FormLabel htmlFor="jobStartTime" text="Week Loads" />
                                    </td>
                                    <td style={{ padding: '8px 4px 4px' }}>
                                        <span className="form-control-sm form-control-plaintext">
                                            {weeklyTotals?.loads}
                                        </span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
          
                    <CollapseUnderlineHeader
                        className="timesheet-details-collapse"
                        headerText='Charges'
                        isOpen={chargesOpen}
                        toggleCollapse={() => this.setState({
                            chargesOpen: !chargesOpen,
                        })}
                    >

                        
                        <div className="details table-responsive">
                            <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                <thead className="text-muted">
                                    <tr>
                                        <th className="isadjusted" />
                                        <th className="detailscharge">Charge</th>
                                        <th className="detailsnote">Equipment</th>
                                        <th className="detailsnote">Notes</th>
                                        <th className="detailsstart">Start</th>
                                        <th className="detailsend">End</th>
                                        <th className="detailsend">Quantity</th>
                                        <th className="detailsunits">Units</th>
                                        <th className="detailsunits">Status</th>
                                        <th className="detailsunits"></th>
                                        <th className="detailsunits"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {charges.map((d, idx) => (
                                        <tr key={d.id}
                                            className={`timesheet-details-table-row${d.removed ? ' strikeout' : ''}`}>

                                            <td className="isadjusted">
                                                {!!d.isAdjustment && (
                                                    <Badge className="p-1">
                                                        ADJ
                                                    </Badge>
                                                )}
                                            </td>
                                            <td>{d.chargeTypeName}</td>
                                            <td>{d.equipmentName}</td>
                                            <td>
                                                {!!d.notes && (
                                                    <div className="userNote">
                                                        <div className="noteContent">
                                                            {
                                                                d.notes
                                                            }
                                                        </div>
                                                        <div className="noteContentByLine">
                                                            {`${d.createdBy}, ${moment(d.createdOn).fromNow()}`}
                                                        </div>
                                                    </div>
                                                )}
                                            </td>
                                            <td align="right">{moment(d.startDate).format('h:mm a')}</td>
                                            <td align="right">{moment(d.endDate).format('h:mm a')}</td>
                                            <td align="right">{d.value}</td>
                                            <td align="center">Hours</td>

                                            {d.status == 'Submitted' &&
                                                <td align="center" style={{ color: 'blue' }}>{d.status}</td>
                                            }

                                            {d.status == 'Unsubmitted' &&
                                                <td align="center" style={{ color: 'red' }}>{d.status}</td>
                                            }

                                            {d.status != 'Unsubmitted' && d.status != 'Submitted' &&
                                                <td align="center">{d.status}</td>
                                            }

                                            <td className="detailsdelete">
                                                {d.status == 'Submitted' && (

                                                    <FontAwesomeIcon
                                                        className={`details-edit-button mt-1 cursor-pointer${d.removed ? ' edit' : ''}`}
                                                        title="edit this charge"
                                                        icon={faPencil}
                                                        onClick={() => this.editInternalCharge(d)}
                                                    />
                                                )}
                                            </td>
                                            <td className="detailsdelete">
                                                {d.status == 'Submitted' && (
                                                    <FontAwesomeIcon
                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${d.removed ? ' removed' : ''}`}
                                                        title="Strike this charge"
                                                        icon={faTrash}
                                                        onClick={() => this.removeCharge(d.id)}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colspan="4">&nbsp;</td>
                                        <td align="right"><b>Total</b></td>
                                        <td align="right"><b>{totalCharges.toFixed(2)}</b></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        {isAdjustingCharge && (
                            <div className="details">
                                <small className="text-warning font-weight-bold">
                                    ADD ADJUSTMENTS
                                </small>
                                <table className="table table-sm table-bordered table-striped adjustment-table">
                                    <thead className="text-muted">
                                        <tr>
                                            <th className="detailscharge">
                                                Charge
                                            </th>
                                            <th className="detailsnote">
                                                Equipment
                                            </th>
                                            <th className="detailsnote">
                                                Notes
                                            </th>
                                            <th className="detailsstart">
                                                Start
                                            </th>
                                            <th className="detailsend">
                                                End
                                            </th>
                                            <th className="detailsqty">
                                                Quantity
                                            </th>
                                            <th className="detailsunits">
                                                Units
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <FormGroup className="mb-0">

                                                    <ValidatedSelect
                                                        styles={CompactSelectStyles}
                                                        minMenuHeight={100}
                                                        maxMenuHeight={200}
                                                        name="adjustmentResourceDropdown"
                                                        required
                                                        hideClear
                                                        options={nonFlaggingContract.contractChargeTypes}
                                                        getOptionLabel={(option) => option.description}
                                                        getOptionValue={(option) => option.chargeTypeId}
                                                        value={(nonFlaggingContract.contractChargeTypes ?? []).find((x) => x.chargeTypeId === timesheetChargeAdjustment.resourceId) ?? ''}
                                                        onChange={(selection, event) => {
                                                            timesheetChargeAdjustment.resourceId = selection.chargeTypeId;
                                                            timesheetChargeAdjustment.resourceName = selection.description;
                                                            timesheetChargeAdjustment.accountCodingTypeId = selection.accountCodingTypeId;
                                                            this.setState({ dailyEntries });
                                                        }}
                                                        validationMessage="Charge is required."
                                                    />
                                                    
                                                </FormGroup>
                                            </td>
                                            <td>
                                                <FormGroup className="mb-0">

                                                    {(timesheetChargeAdjustment.accountCodingTypeId == 2 || timesheetChargeAdjustment.accountCodingTypeId == 5 || timesheetChargeAdjustment.accountCodingTypeId == 4) &&
                                                        <ValidatedSelect
                                                            styles={CompactSelectStyles}
                                                            minMenuHeight={100}
                                                            maxMenuHeight={200}
                                                            name="adjustmentEquipmentDropdown"
                                                            hideClear
                                                            required
                                                            options={equipment}
                                                            getOptionLabel={(option) => option.description}
                                                            getOptionValue={(option) => option.id}
                                                            value={(equipment ?? []).find((x) => x.id === timesheetChargeAdjustment.equipmentId) ?? ''}
                                                            onChange={(selection, event) => {
                                                                timesheetChargeAdjustment.equipmentId = selection.id;
                                                                timesheetChargeAdjustment.equipmentName = selection.description;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                            validationMessage="Equipment is required."
                                                        />
                                                    }

                                                    {(timesheetChargeAdjustment.accountCodingTypeId != 2 && timesheetChargeAdjustment.accountCodingTypeId != 5 && timesheetChargeAdjustment.accountCodingTypeId != 4) &&
                                                        <ValidatedSelect
                                                            styles={CompactSelectStyles}
                                                            minMenuHeight={100}
                                                            maxMenuHeight={200}
                                                            name="adjustmentEquipmentDropdown"
                                                            hideClear
                                                            options={equipment}
                                                            getOptionLabel={(option) => option.description}
                                                            getOptionValue={(option) => option.id}
                                                            value={(equipment ?? []).find((x) => x.id === timesheetChargeAdjustment.equipmentId) ?? ''}
                                                            onChange={(selection, event) => {
                                                                timesheetChargeAdjustment.equipmentId = selection.id;
                                                                timesheetChargeAdjustment.equipmentName = selection.description;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    }


                                                </FormGroup>
                                            </td>
                                            <td>
                                                <FormGroup className="mb-0">
                                                    <Input
                                                        required
                                                        type="textarea"
                                                        name="notes"
                                                        value={timesheetChargeAdjustment.notes}
                                                        onChange={(evt) => {
                                                            const { value } = evt.target;
                                                            timesheetChargeAdjustment.notes = value;
                                                            this.setState({ dailyEntries });
                                                        }}
                                                        validationMessage="Notes are required."
                                                    />
                                                </FormGroup>
                                            </td>
                                            <td style={{ minWidth: '120px' }}>
                                                <TimeEntryCombo
                                                    name="start"
                                                    value={
                                                        timesheetChargeAdjustment.start
                                                    }
                                                    increment={timesheetChargeAdjustment.chargeTypeObj?.chargeTypeIncrement ?? timesheetChargeAdjustment.chargeTypeObj?.increment ?? 0.083}
                                                    onChange={(ev) => this.onInternalChargeTimeEntryChanged(ev)}
                                                    validationMessage="Start is required."
                                                />
                                                {this.renderStartDateDropdown(
                                                    timesheetChargeAdjustment,
                                                    () => this.setState({ timesheetChargeAdjustment })
                                                )}
                                            </td>
                                            <td style={{ minWidth: '120px' }}>
                                                <TimeEntryCombo
                                                    name="end"
                                                    value={
                                                        timesheetChargeAdjustment.end
                                                    }
                                                    increment={timesheetChargeAdjustment.chargeTypeObj?.chargeTypeIncrement ?? timesheetChargeAdjustment.chargeTypeObj?.increment ?? 0.083}
                                                    onChange={(ev) => this.onInternalChargeTimeEntryChanged(ev)}
                                                    validationMessage="End is required."
                                                />
                                                {this.renderEndDateDropdown(
                                                    timesheetChargeAdjustment,
                                                    () => this.setState({ timesheetChargeAdjustment })
                                                )}
                                            </td>
                                            <td>
                                                <NumericInput
                                                    className="form-control-sm text-right"
                                                    name="quantity"
                                                    required
                                                    value={timesheetChargeAdjustment.quantity}
                                                    type="number"
                                                    preventNegative
                                                    onChange={(event) => {
                                                        const val = event.target.value;
                                                        timesheetChargeAdjustment.quantity = val;

                                                        this.setState({ dailyEntries });
                                                    }}
                                                />
                                            </td>
                                            <td align="center">
                                                Hours
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <FlexCenterRow>

                            {!isAdjustingCharge && 
                                <>
                                    <Button
                                        className="mb-2"
                                        size="sm"
                                        type="button"
                                        color="secondary"
                                        onClick={() => {
                                            this.onAdjustInternalCharge();
                                        }}
                                    >
                                        <i className="fa fa-pen-square fa-lg mr-2" />
                                        Add Adjustment
                                    </Button>

                                    &nbsp;&nbsp;

                                    {!allChargesApproved && allChargesSubmitted &&
                                        <Button
                                            disabled={!!saving}
                                            className="mb-2"
                                            size="sm"
                                            color="success"
                                            onClick={() => {
                                                this.approveInternalCharges();
                                            }}
                                        >
                                            Approve
                                        </Button>
                                    }
                                </>
                            }

                            {isAdjustingCharge &&
                                <>
                                    <Button
                                        disabled={!!saving}
                                        className="mb-2"
                                        size="sm"
                                        color="success"
                                        onClick={() => {
                                            this.saveInternalChargeAdjustment();
                                        }}
                                    >
                                        Save
                                    </Button>

                                    &nbsp;&nbsp;

                                    <Button
                                        disabled={!!saving}
                                        className="mb-2"
                                        size="sm"
                                        color="secondary"
                                        onClick={() => {
                                            this.cancelInternalChargeAdjustment();
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            }
                        </FlexCenterRow>

                    </CollapseUnderlineHeader>
                    
                    {jobs.map((j, idx) => {

                        var allTSSubmitted = false;
                        var allTSApproved = false;
                        var isHourlyAdjusting = j.adjusting && j.adjustingUnitsType == 1;

                        const hourlyJobs = (j.scaleTickets ?? []).filter((x) => x.isTonnage == false);
                        var hourlyJobTotal = this.hourlyChargesTotal(j);

                        if (hourlyJobs.length == 0) return (
                            <span></span>
                        );

                        var materials = [];
                        for (var m = 0; m < j.jobMaterials.length; m++) {
                            materials.push({ label: j.jobMaterials[m].description, value: j.jobMaterials[m].id })
                        }

                        return (
                            <CollapseUnderlineHeader
                                className="timesheet-details-collapse"
                                headerText={j.customer + ' - Hourly'}
                                isOpen={true}
                            >

                                <div className="details table-responsive">
                                    <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="isadjusted" />
                                                <th className="detailscharge">Job / Haul #</th>
                                                <th className="detailscharge">Charge</th>
                                                <th className="detailsnote">Resource</th>
                                                <th className="detailsnote">Loads - Material</th>
                                                <th className="detailsnote">Notes</th>
                                                <th className="detailsstart">Start</th>
                                                <th className="detailsend">End</th>
                                                <th className="detailsend">Quantity</th>
                                                <th className="detailsunits">Units</th>
                                                <th className="detailsunits">Status</th>
                                                <th className="detailsunits"></th>
                                                <th className="detailsunits"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {hourlyJobs.map((s, idx) => {

                                                allTSSubmitted = s.charges.length > 0
                                                    && s.charges.filter((x) => x.status == 'Unsubmitted').length == 0;

                                                allTSApproved = (s.charges ?? []).filter((x) => x.status == 'Approved').length == (s.charges ?? []).length;

                                                var isTonnage = s.charges.filter((x) => x.chargeTypeUnitsId == 3).length > 0;

                                                return (
                                                    s.charges.map((c, idx) => (
                                                        <tr key={c.id}
                                                            className={`timesheet-details-table-row${c.removed ? ' strikeout' : ''}`}>

                                                            <td className="isadjusted">
                                                                {!!c.isAdjustment && (
                                                                    <Badge className="p-1">
                                                                        ADJ
                                                                    </Badge>
                                                                )}
                                                            </td>

                                                            <td><span className="site-link" onClick={() => this.onTimesheetEdit(j, s)}>{s.timesheetNumber}</span></td>
                                                            <td>{c.chargeName}</td>
                                                            <td>{c.resourceName}</td>
                                                            <td>{c.resourceTypeId == 1 || c.loads == 0 ? '' : c.materialId > 0 ? c.loads == 1 ? c.loads + ' load - ' + c.materialName : c.loads + ' loads - ' + c.materialName : '' }</td>
                                                            <td>
                                                                {!!c.notes && (
                                                                    <div className="userNote">
                                                                        <div className="noteContent">
                                                                            {
                                                                                c.notes
                                                                            }
                                                                        </div>
                                                                        <div className="noteContentByLine">
                                                                            {`${c.createdBy}, ${moment(c.createdOn).fromNow()}`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.start != null ? moment(c.start).format('h:mm a') : ''}</td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.end != null ? moment(c.end).format('h:mm a') : ''}</td>
                                                            <td align="right">{isTonnage && c.chargeTypeUnitsId == 1 ? '' : c.value}</td>
                                                            <td align="center">{c.chargeTypeUnitsId == 1 ? 'Hours' : 'Tonnage'}</td>

                                                            {c.status == 'Submitted' &&
                                                                <td align="center" style={{ color: 'blue' }}>{c.status}</td>
                                                            }

                                                            {c.status == 'Unsubmitted' &&
                                                                <td align="center" style={{ color: 'red' }}>{c.status}</td>
                                                            }

                                                            {c.status != 'Unsubmitted' && c.status != 'Submitted' &&
                                                                <td align="center">{c.status}</td>
                                                            }
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (

                                                                    <FontAwesomeIcon
                                                                        className={`details-edit-button mt-1 cursor-pointer${c.removed ? ' edit' : ''}`}
                                                                        title="edit this charge"
                                                                        icon={faPencil}
                                                                        onClick={() => this.editCharge(j, s, c, 1)}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    <FontAwesomeIcon
                                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${c.removed ? ' removed' : ''}`}
                                                                        title="Strike this charge"
                                                                        icon={faTrash}
                                                                        onClick={() => this.removeCharge(c.timesheetDetailId)}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )
                                            })}

                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="6">&nbsp;</td>
                                                <td align="right"><b>Total</b></td>
                                                <td align="right"><b>{hourlyJobTotal.toFixed(2)}</b></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {isHourlyAdjusting && (
                                    <div className="details">
                                        <small className="text-warning font-weight-bold">
                                            ADD ADJUSTMENTS
                                        </small>
                                        <table className="table table-sm table-bordered table-striped adjustment-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="detailscharge">
                                                        Job / Haul #
                                                    </th>
                                                    <th className="detailscharge">
                                                        Charge
                                                    </th>
                                                    <th className="detailsname">
                                                        Resource
                                                    </th>
                                                    <th className="detailsnote">
                                                        Notes
                                                    </th>
                                                    <th className="detailsstart">
                                                        Start
                                                    </th>
                                                    <th className="detailsend">
                                                        End
                                                    </th>
                                                    <th className="detailsqty">
                                                        Quantity
                                                    </th>
                                                    <th className="detailsunits">
                                                        Units
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentTimesheetNumberDropdown"
                                                                required
                                                                hideClear
                                                                options={haulNumbers}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(haulNumbers ?? []).find((x) => x.id === j.adjustment.timesheetId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.timesheetId = selection.id;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Haul # is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentChargeTypeDropdown"
                                                                required
                                                                hideClear
                                                                options={chargeTypes}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(chargeTypes ?? []).find((x) => x.id === j.adjustment.chargeTypeId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.chargeTypeId = selection.id;
                                                                    j.adjustment.chargeTypeName = selection.description;
                                                                    j.adjustment.chargeTypeObj = selection;
                                                                    j.adjustment.resourceId = employeeId;
                                                                    j.adjustment.resourceName = employeeName;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Charge type is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 1 &&
                                                                <div style={{ marginTop: '5px' }}>{employeeName}</div>
                                                            }

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                                <ValidatedSelect
                                                                    styles={CompactSelectStyles}
                                                                    minMenuHeight={100}
                                                                    maxMenuHeight={200}
                                                                    name="adjustmentResourceDropdown"
                                                                    required
                                                                    hideClear
                                                                    options={equipment}
                                                                    getOptionLabel={(option) => option.description}
                                                                    getOptionValue={(option) => option.id}
                                                                    value={(equipment ?? []).find((x) => x.id === j.adjustment.resourceId) ?? ''}
                                                                    onChange={(selection, event) => {
                                                                        j.adjustment.resourceId = selection.id;
                                                                        j.adjustment.resourceName = selection.description;

                                                                        this.setState({ dailyEntries });
                                                                    }}
                                                                    validationMessage="Equipment is required."
                                                                />
                                                            }

                                                        </FormGroup>

                                                        {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                            <table width="100%">
                                                                <tr style={{ backgroundColor: 'transparent' }}>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '9px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Material:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <Select
                                                                            styles={CompactSelectStyles}
                                                                            name="adjustmentMaterialDropdown"
                                                                            hideClear
                                                                            options={materials}
                                                                            value={(materials ?? []).find((x) => x.value === j.adjustment.materialId) ?? ''}
                                                                            onChange={(selection, event) => {
                                                                                j.adjustment.materialId = selection.value;
                                                                                j.adjustment.materialName = selection.label;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>

                                                                <tr>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '6px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Loads:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <NumericInput
                                                                            className="form-control-sm"
                                                                            name="loads"
                                                                            required
                                                                            value={j.adjustment?.loads > 0 ? j.adjustment.loads : 0 }
                                                                            type="number"
                                                                            preventNegative
                                                                            onChange={(event) => {
                                                                                const val = event.target.value;
                                                                                j.adjustment.loads = val;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            </table>
                                                        }
                                                        
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <Input
                                                                required
                                                                type="textarea"
                                                                name="notes"
                                                                value={j.adjustment.notes}
                                                                onChange={(evt) => {
                                                                    const { value } = evt.target;
                                                                    j.adjustment.notes = value;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Notes are required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td style={{ minWidth: '120px' }}>
                                                        <TimeEntryCombo
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="start"
                                                            value={
                                                                j.adjustment.start
                                                            }
                                                            increment={j.adjustment.chargeTypeObj?.chargeTypeIncrement ?? j.adjustment.chargeTypeObj?.increment ?? 0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="Start is required."
                                                        />
                                                        {this.renderStartDateDropdown(
                                                            j.adjustment,
                                                            () => this.setState({ dailyEntries })
                                                        )}
                                                    </td>
                                                    <td style={{ minWidth: '120px' }}>
                                                        <TimeEntryCombo
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="end"
                                                            value={
                                                                j.adjustment.end
                                                            }
                                                            increment={j.adjustment.chargeTypeObj?.chargeTypeIncrement ?? j.adjustment.chargeTypeObj?.increment ?? 0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="End is required."
                                                        />
                                                        {this.renderEndDateDropdown(
                                                            j.adjustment,
                                                            () => this.setState({ dailyEntries })
                                                        )}
                                                    </td>
                                                    <td>
                                                        <NumericInput
                                                            className="form-control-sm text-right"
                                                            name="quantity"
                                                            required
                                                            value={j.adjustment.quantity}
                                                            type="number"
                                                            preventNegative
                                                            onChange={(event) => {
                                                                const val = event.target.value;
                                                                j.adjustment.quantity = val;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    </td>
                                                    <td align="center">
                                                        {j.adjustment.chargeTypeObj?.units.description ?? 'Hours'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <FlexCenterRow>

                                    {allTSSubmitted && !allTSApproved && !isHourlyAdjusting &&
                                        <>
                                            <Button
                                                className="mb-2"
                                                size="sm"
                                                type="button"
                                                color="secondary"
                                                onClick={() => {
                                                    this.onAdjustClick(j, 1);
                                                }}
                                            >
                                                <i className="fa fa-pen-square fa-lg mr-2" />
                                                Add Adjustment
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.approveHourlyTimesheets(j.scaleTickets[0].linkedTimesheetId);
                                                }}
                                            >
                                                Approve
                                            </Button>
                                        </>
                                    }

                                    {isHourlyAdjusting &&
                                        <>
                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.saveAdjustment(j, false);
                                                }}
                                            >
                                                Save
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    this.cancelAdjustment(dailyEntries, j);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    }
                                </FlexCenterRow>
                            </CollapseUnderlineHeader>
                        );
                    })}  
                   
                    {jobs.map((j, idx) => {

                        var allTSSubmitted = false;
                        var allTSApproved = false;
                        var isTonnageAdjusting = j.adjusting && j.adjustingUnitsType == 3;

                        const tonnageJobs = (j.scaleTickets ?? []).filter((x) => x.isTonnage == true); 
                        var tonnageJobTotal = this.tonnageChargesTotal(j);

                        if (tonnageJobs.length == 0) return (
                            <span></span>
                        );


                        var materials = [];
                        for (var m = 0; m < j.jobMaterials.length; m++) {
                            materials.push({ label: j.jobMaterials[m].description, value: j.jobMaterials[m].id })
                        }

                        return (
                            <CollapseUnderlineHeader
                                className="timesheet-details-collapse"
                                headerText={j.customer + ' - Tonnage'}
                                isOpen={true}
                            >

                                <div className="details table-responsive">
                                    <table className="table table-sm table-bordered table-striped timesheet-details-table">
                                        <thead className="text-muted">
                                            <tr>
                                                <th className="isadjusted" />
                                                <th className="detailscharge">Job / Haul #</th>
                                                <th className="detailscharge">Ticket #</th>
                                                <th className="detailscharge">Charge</th>
                                                <th className="detailsnote">Resource</th>
                                                <th className="detailsnote">Material</th>
                                                <th className="detailsnote">Notes</th>
                                                <th className="detailsstart">Start</th>
                                                <th className="detailsend">End</th>
                                                <th className="detailsend">Quantity</th>
                                                <th className="detailsunits">Units</th>
                                                <th className="detailsunits">Status</th>
                                                <th className="detailsunits"></th>
                                                <th className="detailsunits"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tonnageJobs.map((s, idx) => {

                                                allTSSubmitted = s.charges.length > 0
                                                    && s.charges.filter((x) => x.status == 'Unsubmitted').length == 0;

                                                allTSApproved = (s.charges ?? []).filter((x) => x.status == 'Approved').length == (s.charges ?? []).length;

                                                var isTonnage = s.charges.filter((x) => x.chargeTypeUnitsId == 3).length > 0;

                                                return (
                                                    s.charges.map((c, idx) => (
                                                        <tr key={c.id}
                                                            className={`timesheet-details-table-row${c.removed ? ' strikeout' : ''}`}>
                                                           
                                                            <td className="isadjusted">
                                                                {!!c.isAdjustment && (
                                                                    <Badge className="p-1">
                                                                        ADJ
                                                                    </Badge>
                                                                )}
                                                            </td>
                                                          
                                                            <td><span className="site-link" onClick={() => this.onTimesheetEdit(j, s)}>{s.timesheetNumber}</span></td>
                                                            <td>{s.workOrderNumber}</td>
                                                            <td>{c.chargeName}</td>
                                                            <td>{c.resourceName}</td>
                                                            <td>{c.materialName}</td>
                                                            <td>
                                                                {!!c.notes && (
                                                                    <div className="userNote">
                                                                        <div className="noteContent">
                                                                            {
                                                                                c.notes
                                                                            }
                                                                        </div>
                                                                        <div className="noteContentByLine">
                                                                            {`${c.createdBy}, ${moment(c.createdOn).fromNow()}`}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.start != null ? moment(c.start).format('h:mm a') : ''}</td>
                                                            <td align="right">{c.chargeTypeUnitsId == 1 && c.end != null ? moment(c.end).format('h:mm a') : ''}</td>
                                                            <td align="right">{isTonnage && c.chargeTypeUnitsId == 1  ? '' : c.value}</td>
                                                            <td align="center">{c.chargeTypeUnitsId == 1 ? 'Hours' : 'Tonnage'}</td>

                                                            {c.status == 'Submitted' &&
                                                                <td align="center" style={{ color: 'blue' }}>{c.status}</td>
                                                            }

                                                            {c.status == 'Unsubmitted' &&
                                                                <td align="center" style={{ color: 'red' }}>{c.status}</td>
                                                            }

                                                            {c.status != 'Unsubmitted' && c.status != 'Submitted' &&
                                                                <td align="center">{c.status}</td>
                                                            }
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    
                                                                    <FontAwesomeIcon
                                                                        className={`details-edit-button mt-1 cursor-pointer${c.removed ? ' edit' : ''}`}
                                                                        title="edit this charge"
                                                                        icon={faPencil}
                                                                        onClick={() => this.editCharge(j, s, c, 3)}
                                                                    />
                                                                )}
                                                            </td>
                                                            <td className="detailsdelete">
                                                                {c.status == 'Submitted' && (
                                                                    <FontAwesomeIcon
                                                                        className={`details-remove-button text-danger mt-1 cursor-pointer${c.removed ? ' removed' : ''}`}
                                                                        title="Strike this charge"
                                                                        icon={faTrash}
                                                                        onClick={() => this.removeCharge(c.timesheetDetailId)}
                                                                    />
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )
                                            })}

                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <td colspan="6">&nbsp;</td>
                                                <td align="right"><b>Total</b></td>
                                                <td align="right"><b>{tonnageJobTotal.toFixed(2)}</b></td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>

                                {isTonnageAdjusting && (
                                    <div className="details">
                                        <small className="text-warning font-weight-bold">
                                            ADD ADJUSTMENTS
                                        </small>
                                        <table className="table table-sm table-bordered table-striped adjustment-table">
                                            <thead className="text-muted">
                                                <tr>
                                                    <th className="detailscharge">
                                                        Job / Haul #
                                                    </th>
                                                    <th className="detailscharge">
                                                        Charge
                                                    </th>
                                                    <th className="detailsname">
                                                        Resource
                                                    </th>
                                                    <th className="detailsnote">
                                                        Notes
                                                    </th>
                                                    <th className="detailsstart">
                                                        Start
                                                    </th>
                                                    <th className="detailsend">
                                                        End
                                                    </th>
                                                    <th className="detailsqty">
                                                        Quantity
                                                    </th>
                                                    <th className="detailsunits">
                                                        Units
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentTimesheetNumberDropdown"
                                                                required
                                                                hideClear
                                                                options={haulNumbers}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(haulNumbers ?? []).find((x) => x.id === j.adjustment.timesheetId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.timesheetId = selection.id;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Haul # is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <ValidatedSelect
                                                                styles={CompactSelectStyles}
                                                                minMenuHeight={100}
                                                                maxMenuHeight={200}
                                                                name="adjustmentChargeTypeDropdown"
                                                                required
                                                                hideClear
                                                                options={chargeTypes}
                                                                getOptionLabel={(option) => option.description}
                                                                getOptionValue={(option) => option.id}
                                                                value={(chargeTypes ?? []).find((x) => x.id === j.adjustment.chargeTypeId) ?? ''}
                                                                onChange={(selection, event) => {
                                                                    j.adjustment.chargeTypeId = selection.id;
                                                                    j.adjustment.chargeTypeName = selection.description;
                                                                    j.adjustment.chargeTypeObj = selection;
                                                                    j.adjustment.resourceId = employeeId;
                                                                    j.adjustment.resourceName = employeeName;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Charge type is required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 1 &&
                                                                <div style={{ marginTop: '5px' }}>{employeeName}</div>
                                                            }

                                                            {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                                <ValidatedSelect
                                                                    styles={CompactSelectStyles}
                                                                    minMenuHeight={100}
                                                                    maxMenuHeight={200}
                                                                    name="adjustmentResourceDropdown"
                                                                    required
                                                                    hideClear
                                                                    options={equipment}
                                                                    getOptionLabel={(option) => option.description}
                                                                    getOptionValue={(option) => option.id}
                                                                    value={(equipment ?? []).find((x) => x.id === j.adjustment.resourceId) ?? ''}
                                                                    onChange={(selection, event) => {
                                                                        j.adjustment.resourceId = selection.id;
                                                                        j.adjustment.resourceName = selection.description;

                                                                        this.setState({ dailyEntries });
                                                                    }}
                                                                    validationMessage="Equipment is required."
                                                                />
                                                            }

                                                        </FormGroup>

                                                        {j.adjustment.chargeTypeObj?.resourceTypeId == 2 &&
                                                            <table width="100%">
                                                                <tr style={{ backgroundColor: 'transparent' }}>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '9px' }} align="right">
                                                                        <span style={{ fontSize: '12px' }}>Material:&nbsp;</span>
                                                                    </td>
                                                                    <td style={{ padding: 0, border: 0, paddingTop: '4px' }}>
                                                                        <Select
                                                                            styles={CompactSelectStyles}
                                                                            name="adjustmentMaterialDropdown"
                                                                            hideClear
                                                                            options={materials}
                                                                            value={(materials ?? []).find((x) => x.value === j.adjustment.materialId) ?? ''}
                                                                            onChange={(selection, event) => {
                                                                                j.adjustment.materialId = selection.value;
                                                                                j.adjustment.materialName = selection.label;

                                                                                this.setState({ dailyEntries });
                                                                            }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            </table>
                                                        }
                                                    </td>
                                                    <td>
                                                        <FormGroup className="mb-0">
                                                            <Input
                                                                required
                                                                type="textarea"
                                                                name="notes"
                                                                value={j.adjustment.notes}
                                                                onChange={(evt) => {
                                                                    const { value } = evt.target;
                                                                    j.adjustment.notes = value;
                                                                    this.setState({ dailyEntries });
                                                                }}
                                                                validationMessage="Notes are required."
                                                            />
                                                        </FormGroup>
                                                    </td>
                                                    <td style={{ minWidth: '120px' }}>
                                                        <TimeEntryCombo
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="start"
                                                            value={
                                                                j.adjustment.start
                                                            }
                                                            increment={j.adjustment.chargeTypeObj?.chargeTypeIncrement ?? j.adjustment.chargeTypeObj?.increment ?? 0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                            validationMessage="Start is required."
                                                        />
                                                        {this.renderStartDateDropdown(
                                                            j.adjustment,
                                                            () => this.setState({ dailyEntries })
                                                        )}
                                                    </td>
                                                    <td style={{ minWidth: '120px' }}>
                                                        <TimeEntryCombo
                                                            disabled={j.adjustment.chargeTypeObj?.unitsId != 1}
                                                            name="end"
                                                            value={
                                                                j.adjustment.end
                                                            }
                                                            increment={j.adjustment.chargeTypeObj?.chargeTypeIncrement ?? j.adjustment.chargeTypeObj?.increment ?? 0.083}
                                                            onChange={(ev) => this.onAdjustmentTimeEntryChanged(dailyEntries, j, ev)}
                                                        />
                                                        {this.renderEndDateDropdown(
                                                            j.adjustment,
                                                            () => this.setState({ dailyEntries })
                                                        )}
                                                    </td>
                                                    <td>
                                                        <NumericInput
                                                            className="form-control-sm text-right"
                                                            name="quantity"
                                                            value={j.adjustment.quantity ?? 0}
                                                            type="number"
                                                            preventNegative
                                                            onChange={(event) => {
                                                                const val = event.target.value;
                                                                j.adjustment.quantity = val;

                                                                this.setState({ dailyEntries });
                                                            }}
                                                        />
                                                    </td>
                                                    <td align="center">
                                                        {j.adjustment.chargeTypeObj?.units.description ?? 'Hours'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <FlexCenterRow>

                                    {allTSSubmitted && !allTSApproved && !isTonnageAdjusting &&
                                        <>
                                            <Button
                                                className="mb-2"
                                                size="sm"
                                                type="button"
                                                color="secondary"
                                                onClick={() => {
                                                    this.onAdjustClick(j, 3);
                                                }}
                                            >
                                                <i className="fa fa-pen-square fa-lg mr-2" />
                                                Add Adjustment
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.approveTonnageTimesheets(j.scaleTickets[0].linkedTimesheetId);
                                                }}
                                            >
                                                Approve
                                            </Button>
                                        </>
                                    }

                                    {isTonnageAdjusting &&
                                        <>
                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="success"
                                                onClick={() => {
                                                    this.saveAdjustment(j, true);
                                                }}
                                            >
                                                Save
                                            </Button>

                                            &nbsp;&nbsp;

                                            <Button
                                                disabled={!!saving}
                                                className="mb-2"
                                                size="sm"
                                                color="secondary"
                                                onClick={() => {
                                                    this.cancelAdjustment(dailyEntries, j);
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </>
                                    }
                                </FlexCenterRow>
                            </CollapseUnderlineHeader>
                        );
                    })}    


                    
                </SlideForm>

                <TimesheetEdit
                    id="timeshetEdit"
                    ref={this.timesheetEditRef}
                />

            </>
        );
    }
}
